import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import sqliteImmediateStorage from "./sqliteImmediateStorage";

// DI-05: Simple mutex to prevent concurrent read-modify-write on documentSequence.
// Even in single-tab, auto-sync + manual order can race.
let _docSeqLock = false;

const DEFAULTS = {
  tillSession: null,
  documentSequence: 0,
  locked: false,
  unlink: false,
  nextBusinessDate: null,
  parkedList: [],
  lastUpdatedTime: null,
  // Mid-transaction recovery — persisted so payment resumes after crash/reload
  paymentInProgress: false,
  paymentSnapshot: null, // { selectedMethod, amount, partialPayments }
};

const useSessionStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...DEFAULTS,
        _hydrated: false,

        // Actions
        setTillSession: (session) => set({ tillSession: session }),
        updateTillSession: (updates) =>
          set((state) => ({
            tillSession: state.tillSession ? { ...state.tillSession, ...updates } : updates,
          })),

        setDocumentSequence: (seq) => set({ documentSequence: seq }),
        incrementDocumentSequence: () => {
          // DI-05: Mutex prevents concurrent read-modify-write producing duplicate sequence numbers.
          if (_docSeqLock) {
            console.warn("[DI-05] Document sequence increment already in progress — waiting");
            // Spin-wait briefly (synchronous — JS is single-threaded so this only guards
            // against re-entrant calls from the same tick, e.g. via zustand middleware).
            let waited = 0;
            while (_docSeqLock && waited < 50) {
              waited++;
            }
            if (_docSeqLock) {
              console.error("[DI-05] Document sequence lock not released — forcing through to avoid deadlock");
            }
          }
          _docSeqLock = true;
          try {
            const current = get().documentSequence || 0;
            const next = current + 1;
            set({ documentSequence: next });
            return next;
          } finally {
            _docSeqLock = false;
          }
        },

        setLocked: (val) => set({ locked: val }),
        setUnlink: (val) => set({ unlink: val }),
        setNextBusinessDate: (date) => set({ nextBusinessDate: date }),
        setLastUpdatedTime: (time) => set({ lastUpdatedTime: time }),

        // Mid-transaction recovery
        setPaymentInProgress: (val) => set({ paymentInProgress: val }),
        setPaymentSnapshot: (snapshot) => set({ paymentSnapshot: snapshot }),
        clearPaymentState: () => set({ paymentInProgress: false, paymentSnapshot: null }),

        setParkedList: (list) => set({ parkedList: list }),
        addParkedBill: (bill) =>
          set((state) => {
            const list = [...(state.parkedList || []), bill];
            if (list.length > 100) list.shift();
            return { parkedList: list };
          }),
        removeParkedBill: (key) =>
          set((state) => ({
            parkedList: (state.parkedList || []).filter((b) => b.parkedDocNo !== key && b.key !== key),
          })),

        clearSession: () => set({ ...DEFAULTS }),
      }),
      {
        name: "sessionStore",
        storage: createJSONStorage(() => sqliteImmediateStorage),
        skipHydration: true,
        partialize: (state) => ({
          tillSession: state.tillSession,
          documentSequence: state.documentSequence,
          locked: state.locked,
          unlink: state.unlink,
          nextBusinessDate: state.nextBusinessDate,
          parkedList: state.parkedList,
          lastUpdatedTime: state.lastUpdatedTime,
        }),
        merge: (persisted, current) => ({
          ...current,
          ...DEFAULTS,
          ...(persisted || {}),
          _hydrated: true,
        }),
      }
    )
  )
);

export default useSessionStore;
